/**
 * Canvas Drag Hook.
 *
 * Encapsulates drag-to-move logic for yard sections on the SVG canvas.
 * Handles pointer capture, position updates in local state during drag,
 * snap alignment with other sections and yard boundaries, and persists
 * the final position to the backend on pointer-up.
 */

import { useState, useCallback, useRef } from "react";
import { findSnapGuides, getBoundaryDistances } from "./shapeUtils";
import type { SnapGuide, BoundaryDistance } from "./shapeUtils";

interface DragState {
  sectionId: string;
  startX: number;
  startY: number;
  offsetX: number;
  offsetY: number;
}

interface SectionPosition {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

interface UseCanvasDragOptions {
  sections: SectionPosition[];
  yardWidth: number;
  yardHeight: number;
  onDragEnd: (sectionId: string, x: number, y: number) => void;
}

export function useCanvasDrag({
  sections,
  yardWidth,
  yardHeight,
  onDragEnd,
}: UseCanvasDragOptions) {
  const [dragOffset, setDragOffset] = useState<{
    id: string;
    dx: number;
    dy: number;
  } | null>(null);
  const [guides, setGuides] = useState<SnapGuide[]>([]);
  const [boundaries, setBoundaries] = useState<BoundaryDistance[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<DragState | null>(null);

  const handlePointerDown = useCallback(
    (sectionId: string, e: React.PointerEvent) => {
      e.stopPropagation();
      e.preventDefault();
      const svg = (e.target as SVGElement).ownerSVGElement;
      if (!svg) return;

      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const svgPt = pt.matrixTransform(svg.getScreenCTM()?.inverse());

      const section = sections.find((s) => s.id === sectionId);
      if (!section) return;

      dragRef.current = {
        sectionId,
        startX: section.x,
        startY: section.y,
        offsetX: svgPt.x - section.x,
        offsetY: svgPt.y - section.y,
      };
      setIsDragging(true);
      (e.target as SVGElement).setPointerCapture(e.pointerId);
    },
    [sections],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragRef.current) return;

      const svg =
        (e.target as SVGElement).ownerSVGElement ??
        (e.target as unknown as SVGSVGElement);
      if (!svg || !("createSVGPoint" in svg)) return;

      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      const svgPt = pt.matrixTransform(svg.getScreenCTM()?.inverse());

      const drag = dragRef.current;
      let newX = svgPt.x - drag.offsetX;
      let newY = svgPt.y - drag.offsetY;

      const section = sections.find((s) => s.id === drag.sectionId);
      if (!section) return;

      // Clamp to yard boundaries
      newX = Math.max(0, Math.min(yardWidth - section.width, newX));
      newY = Math.max(0, Math.min(yardHeight - section.height, newY));

      // Find snap guides
      const {
        guides: newGuides,
        snappedX,
        snappedY,
      } = findSnapGuides(
        newX,
        newY,
        section.width,
        section.height,
        drag.sectionId,
        sections,
        yardWidth,
        yardHeight,
      );

      // Compute boundary distances
      const newBoundaries = getBoundaryDistances(
        snappedX,
        snappedY,
        section.width,
        section.height,
        yardWidth,
        yardHeight,
      );

      setDragOffset({
        id: drag.sectionId,
        dx: snappedX - drag.startX,
        dy: snappedY - drag.startY,
      });
      setGuides(newGuides);
      setBoundaries(newBoundaries);
    },
    [sections, yardWidth, yardHeight],
  );

  const handlePointerUp = useCallback(() => {
    if (!dragRef.current || !dragOffset) {
      dragRef.current = null;
      setIsDragging(false);
      return;
    }

    const drag = dragRef.current;
    const finalX = drag.startX + dragOffset.dx;
    const finalY = drag.startY + dragOffset.dy;

    onDragEnd(drag.sectionId, finalX, finalY);

    dragRef.current = null;
    setDragOffset(null);
    setGuides([]);
    setBoundaries([]);
    setIsDragging(false);
  }, [dragOffset, onDragEnd]);

  /**
   * Get the display position for a section (original + drag offset if active).
   */
  const getSectionPosition = useCallback(
    (sectionId: string, origX: number, origY: number) => {
      if (dragOffset && dragOffset.id === sectionId) {
        return { x: origX + dragOffset.dx, y: origY + dragOffset.dy };
      }
      return { x: origX, y: origY };
    },
    [dragOffset],
  );

  return {
    isDragging,
    dragSectionId: dragRef.current?.sectionId ?? null,
    guides,
    boundaries,
    getSectionPosition,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
  };
}
